<template>
	<JBox
		@click.stop="$emit('node:click', node)"
		@mouseover.self="$emit('node:mouseover', node)"
		@mouseleave.self="$emit('node:mouseleave', node)"
		:class="computedClasses"
		:style="computedStyle"
	>
		<JBox class="s-select-none s-flex s-absolute s-top-0 s-left-0 s-m-1 draggable-handle s-cursor-move">
			<!-- Label -->
			<JBox
				class="s-shadow s-flex s-items-center s-overflow-hidden s-h-4 s-px-1 s-leading-normal s-bg-white s-text-black s-uppercase s-rounded-sm s-font-bold s-text-xs"
			>
				<CIcon name="draggable-handle" class="s-text-gray-500 s-fill-current s-w-1 s-mr-1" />
				{{ node.component }}
			</JBox>
		</JBox>

		<!-- Expand children -->
		<JBox
			v-if="node.children && node.children.length"
			@click.stop="$emit('nodeExpand:click', node)"
			class="s-shadow s-bg-white s-cursor-pointer s-flex s-justify-center s-items-center s-fill-current s-text-black s-rounded-sm s-w-3 s-h-3 s-absolute s-top-0 s-right-0 s-m-1"
		>
			<svg
				class="s-ease-in-out s-duration-200"
				:class="{ 'transform rotate-180': isExpanded }"
				xmlns="http://www.w3.org/2000/svg"
				height="14"
				viewBox="0 0 24 24"
				width="14"
			>
				<path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
				<path d="M0 0h24v24H0z" fill="none" />
			</svg>
		</JBox>

		<!-- Children -->
		<template v-if="node.children">
			<JDraggable
				v-model="node.children"
				v-bind="draggableSettings"
				:class="applyLayoutClasses(node)"
				@change="$emit('nodeDrag:change')"
			>
				<template v-if="isExpanded">
					<UiEditorLayoutNode
						v-for="item in node.children"
						:key="item.id"
						:node="item"
						:draggableSettings="draggableSettings"
						:layer="layer + 1"
						v-on="$listeners"
					/>
				</template>
			</JDraggable>
		</template>
	</JBox>
</template>

<script>
// MOCK: classes could possinly have a prefix
// We are using Studio classes but later on we should generate classes based on app theming
const prefix = ""

export default {
	name: "UiEditorLayoutNode",
	props: {
		node: {
			type: Object,
			default: () => ({})
		},

		draggableSettings: {
			type: Object,
			default: () => ({})
		},

		layer: {
			type: Number,
			default: 0
		}
	},

	computed: {
		isSelected() {
			return this.$store.state?.uiEditor?.selectedNodeIds?.includes(this?.node?.id)
		},

		isHovered() {
			return this.$store.state?.uiEditor?.hoveredNodeId === this?.node?.id
		},

		isExpanded() {
			return this.$store.state?.uiEditor?.expandedNodeIds?.includes(this?.node?.id)
		},

		computedClasses() {
			const staticClases = `s-p-2 s-pt-7 s-pb-3 s-mb-3 s-border s-rounded-sm s-relative`
			return `${this.getBackgroundColor()} ${this.getBorderColor()} ${staticClases}`
		},

		computedStyle() {
			let output = ``

			if (this.isSelected) output += `outline: 3px #d200ff solid; `
			if (this.isHovered && !this.isSelected) output += ` outline: 2px #d200ff dotted; `
			if (this.node.bindTo) output += ` background: rgb(255, 168, 106); border-color: rgb(255, 168, 106); `

			return output
		}
	},

	methods: {
		applyLayoutClasses() {
			const node = this.node
			const layoutClassesPrefixes = [`${prefix}flex`, `${prefix}block`, `${prefix}justify`, `${prefix}items-`]
			const classArray = (node?.attrsMap?.class ?? "").split(" ")
			const classesToApply = classArray.filter(cls => layoutClassesPrefixes.find(item => cls.includes(item))) || []

			return classesToApply.join(" ")
		},

		applySizeClasses() {
			const node = this.node
			// TODO: naive approach, clashes e.g. with shadow-
			// qucikly putting unwanted to filter final classes
			const sizeClassesPrefixes = [`${prefix}w-`]
			const unwanted = [`${prefix}shadow-`]
			const classArray = (node?.attrsMap?.class ?? "").split(" ")

			const classesToApply =
				classArray
					.filter(cls => !unwanted.find(unw => cls.includes(unw)))
					.filter(cls => sizeClassesPrefixes.find(item => cls.includes(item))) || []

			return classesToApply.join(" ")
		},

		getBackgroundColor() {
			// NOTE: We are listing all the classes here so that if we used PURGE CSS, it would work
			const possibleColors = [
				"s-bg-purple-100",
				"s-bg-purple-200",
				"s-bg-purple-300",
				"s-bg-purple-400",
				"s-bg-purple-500",
				"s-bg-purple-600",
				"s-bg-purple-700",
				"s-bg-purple-800",
				"s-bg-purple-900"
			]

			// TODO: When we havw more than 9 layers, we should start counting from beggining again
			return this.layer > -1 && this.layer < 9 ? possibleColors[this.layer] : possibleColors[8]
		},

		getBorderColor() {
			// NOTE: We are listing all the classes here so that if we used PURGE CSS, it would work
			const possibleColors = [
				"s-border-purple-100",
				"s-border-purple-200",
				"s-border-purple-300",
				"s-border-purple-400",
				"s-border-purple-500",
				"s-border-purple-600",
				"s-border-purple-700",
				"s-border-purple-800",
				"s-border-purple-900"
			]

			// TODO: When we havw more than 9 layers, we should start counting from beggining again
			return this.layer > -1 && this.layer < 8 ? possibleColors[this.layer + 1] : possibleColors[8]
		}
	}
}
</script>
